/**
 * The one place an instant becomes a calendar day (010 FR-006, SC-009).
 *
 * Before this feature there were four: `date-cell.ts` compared the browser's local day,
 * `review-due.ts` had its own `localCalendarDay`, `separators.ts` had `startOfDay`, and the archive
 * and review pages each reduced `CLOCK()` their own way. They agreed only for a practitioner on
 * UTC, which is the defect this feature exists to fix — a row could be banded against one day and
 * separated under another on the same screen. `core/time/no-second-reduction.spec.ts` is what stops
 * a fifth appearing.
 *
 * **The result is midnight UTC of that day, not local midnight.** Every day-valued date in the
 * application is already stored and read that way (`shared/actions/model/dates.ts`), so the two are
 * directly comparable and `then - today` is exact whole days regardless of any DST transition
 * between them. Returning local midnight instead would put a conversion at every comparison site,
 * and that conversion is precisely what this repository has got wrong twice.
 */

/**
 * One formatter per zone. Constructing an `Intl.DateTimeFormat` reads the zone database, and this
 * function runs once per row on every list the application draws; the map is what lets the single
 * reduction point be called from a row renderer without thinking about it.
 *
 * Unbounded by design: the key space is the zones a single practitioner's session actually asks
 * about, which is one, plus whatever a test names.
 */
const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(zone: string): Intl.DateTimeFormat {
  const cached = formatters.get(zone);
  if (cached) {
    return cached;
  }

  // `en-US` is not a display choice — nothing here is shown to anyone. It is a locale whose
  // numbering system is Latin, so the parts parse as integers; asking for the viewer's locale would
  // hand back Arabic-Indic or Devanagari digits for some of them and `Number()` would yield NaN.
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  formatters.set(zone, formatter);
  return formatter;
}

/** Midnight **UTC** of the calendar day `instant` falls on in `zone`. */
export function calendarDayIn(instant: Date, zone: string): Date {
  const parts = formatterFor(zone).formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((candidate) => candidate.type === type)!.value);

  return new Date(Date.UTC(part('year'), part('month') - 1, part('day')));
}

/**
 * A midnight-UTC day as the wire spells it, `YYYY-MM-DD`.
 *
 * Here rather than beside its callers for `calendarDayIn`'s reason: 012 wrote this expression
 * twice in one branch — once in `shared/actions/model/dates.ts` and once privately in
 * `features/recurring/recurring.store.ts` — and `no-second-reduction.spec.ts` could not see it,
 * because neither copy uses a local getter or an unargumented `new Date()`. The guard now bans
 * the expression itself outside this folder.
 */
export const isoDay = (day: Date): string => day.toISOString().slice(0, 10);
